import React, { useCallback, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useAppSelector } from '@/redux/hooks';
import { selectPackagingTimelineScale } from '@/redux/selectors/packagingSelectors';
import { PIXELS_PER_MINUTE, SEGMENT_CONTAINER_PADDING, TIMELINE_SIDES_PADDING } from '@/components/Packaging/constants';
import { formatDuration } from '@/components/Packaging/utils';
import useResizeObserver from '@/components/_hooks/useResizeObserver';

// "Segment" in the context of ruler is the distance between two time stamps.
// When timeline scale equals to 1 segment size would equal to PIXELS_PER_MINUTE.
// When timeline scales segment length changes. At some point segment can become too big or two small, that is when we
// want to make it bigger or smaller by dividing it by another scale.
// `segmentsScaleMap` describes what scale we should apply to segment depending on its size.
// For example when segment size is less than 15 the applied scale is 0.1; when segment size is less than 70  the applied scale is 0.2.
const segmentsScaleMap = [
  [15, 0.1], // 10 minutes
  [70, 0.2], // 5 minutes
  [200, 1], // 1 minute
  [400, 2], // 30 seconds
  [800, 6], // 10 seconds
  [4000, 12], // 5 seconds
  [Infinity, 60] // 1 seconds
];

// Finds a scale based on segment size. Read comment above for better understanding.
const findScale = (segmentSize: number) => {
  const sizeToScale = segmentsScaleMap.find((sizeToScale) => {
    return sizeToScale[0] > segmentSize;
  });

  return sizeToScale !== undefined ? sizeToScale[1] : 1;
};

interface Props {
  timelineScrollPosition: number;
}

const Ruler: React.FC<Props> = ({ timelineScrollPosition }) => {
  const timelineScale = useAppSelector(selectPackagingTimelineScale);
  const containerRef = useRef<HTMLDivElement>(null);

  const [rulerWidth, setRulerWidth] = useState(1920);

  let segmentLength = timelineScale * PIXELS_PER_MINUTE;

  const segmentScale = findScale(segmentLength);
  segmentLength /= segmentScale;

  const timeStampsCount = Math.ceil(rulerWidth / segmentLength) + 1;
  const segmentsSkipped = Math.max(Math.floor(timelineScrollPosition / segmentLength) - 1, 0);

  const handleContainerResize = useCallback((resizeObserverEntry: ResizeObserverEntry) => {
    setRulerWidth(resizeObserverEntry.contentRect.width);
  }, []);

  useResizeObserver({
    ref: containerRef,
    onResize: handleContainerResize
  });

  return (
    <Wrapper>
      <Absolute
        ref={containerRef}
        style={{
          left: `${TIMELINE_SIDES_PADDING + SEGMENT_CONTAINER_PADDING - timelineScrollPosition + segmentsSkipped * segmentLength}px`
        }}
      >
        <FirstSmallMark />
        <Clip>
          <Border />
          <SmallMarks segmentLength={segmentLength} />
          <LargeMarks segmentLength={segmentLength} />
          <TimeStamps segmentLength={segmentLength}>
            {Array.from({ length: timeStampsCount }, (_, i) => (
              <TimeStamp key={i} segmentLength={segmentLength}>
                {formatDuration(Math.round(((i + segmentsSkipped + 1) * 60_000) / segmentScale), {
                  hideZeroHours: true
                })}
              </TimeStamp>
            ))}
          </TimeStamps>
        </Clip>
      </Absolute>
    </Wrapper>
  );
};

export default Ruler;

const BORDER_HEIGHT = 6;
const MARKS_HEIGHT = 12;
const TIMESTAMPS_HEIGHT = 17;
const SMALL_MARK_RADIUS = 1;

const Wrapper = styled.div`
  padding-top: 50px;
  padding-bottom: 28px;
  pointer-events: none; // to allow scrolling the timeline when the pointer is over the Ruler
  user-select: none;
`;

const Absolute = styled.div`
  position: absolute;
  right: 0;
`;

const Clip = styled.div`
  overflow: hidden;
  position: relative;
  height: ${BORDER_HEIGHT + MARKS_HEIGHT + TIMESTAMPS_HEIGHT}px;
`;

const Border = styled.div`
  height: ${BORDER_HEIGHT}px;
  background-image: linear-gradient(to right, var(--primary-main) 50%, rgba(255, 255, 255, 0) 0%);
  background-position: top left;
  background-size: 22px 3px;
  background-repeat: repeat-x;
`;

const LargeMarks = styled('div', { shouldForwardProp: (prop) => !['segmentLength'].includes(prop) })<{ segmentLength: number }>`
  height: ${MARKS_HEIGHT}px;
  background: linear-gradient(
      to right,
      transparent ${({ segmentLength }) => (1 - 1 / segmentLength) * 100}%,
      ${({ theme }) => (theme.palette.mode === 'dark' ? '#D9D9D9' : '#262626')} 0%
    )
    0 0 repeat-x;
  background-size: ${({ segmentLength }) => segmentLength}px 12px;
`;

const getSmallMarkRadialGradient = (isDarkMode: boolean) => {
  return `radial-gradient(${SMALL_MARK_RADIUS}px ${SMALL_MARK_RADIUS}px, ${isDarkMode ? '#D9D9D9' : '#262626'} 99%, transparent 100%)`;
};

const FirstSmallMark = styled.div`
  position: absolute;
  top: ${BORDER_HEIGHT}px;
  left: -${SMALL_MARK_RADIUS}px;
  width: ${SMALL_MARK_RADIUS * 2}px;
  height: ${MARKS_HEIGHT}px;
  background-image: ${({ theme }) => getSmallMarkRadialGradient(theme.palette.mode === 'dark')};
`;

const SmallMarks = styled('div', { shouldForwardProp: (prop) => !['segmentLength'].includes(prop) })<{ segmentLength: number }>`
  height: ${MARKS_HEIGHT}px;
  margin-bottom: -${MARKS_HEIGHT}px;
  ${({ theme }) => {
    const rg = getSmallMarkRadialGradient(theme.palette.mode === 'dark');
    // repeat `radial-gradient` 9 times, one for every marker point in the segment
    return `background-image: ${rg}, ${rg}, ${rg}, ${rg}, ${rg}, ${rg}, ${rg}, ${rg}, ${rg};`;
  }}

  ${({ segmentLength: sl }) => {
    return `
      background-size: ${sl}px 100%;
      background-position:
        ${sl / 2 + (sl / 10) * 1 - SMALL_MARK_RADIUS}px 0,
        ${sl / 2 + (sl / 10) * 2 - SMALL_MARK_RADIUS}px 0,
        ${sl / 2 + (sl / 10) * 3 - SMALL_MARK_RADIUS}px 0,
        ${sl / 2 + (sl / 10) * 4 - SMALL_MARK_RADIUS}px 0,
        ${sl / 2 + (sl / 10) * 5 - SMALL_MARK_RADIUS}px 0,
        ${sl / 2 + (sl / 10) * 6 - SMALL_MARK_RADIUS}px 0,
        ${sl / 2 + (sl / 10) * 7 - SMALL_MARK_RADIUS}px 0,
        ${sl / 2 + (sl / 10) * 8 - SMALL_MARK_RADIUS}px 0,
        ${sl / 2 + (sl / 10) * 9 - SMALL_MARK_RADIUS}px 0;
    `;
  }}
`;

const TimeStamps = styled('div', { shouldForwardProp: (prop) => !['segmentLength'].includes(prop) })<{ segmentLength: number }>`
  position: absolute;
  left: ${({ segmentLength }) => segmentLength / 2}px;
  display: flex;
  height: ${TIMESTAMPS_HEIGHT}px;
  overflow: hidden;
`;

const TimeStamp = styled('div', { shouldForwardProp: (prop) => !['segmentLength'].includes(prop) })<{ segmentLength: number }>`
  width: ${({ segmentLength }) => segmentLength}px;
  margin-top: 2px;
  text-align: center;
  font-size: 0.77rem;
  color: ${({ theme }) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)')};
`;
